import React, { useMemo } from 'react'
import { makeStyles } from '@mui/styles'
import PerfectScrollbar from 'react-perfect-scrollbar'
import { HtmlInput, TagSelectInput, ToggleSlider } from '@/modules/base/components/inputs'
import ThemeInterface from '@/theme-interface'
import { user } from 'ampersand-app'
import { experimentalFeatures } from '@/modules/base/util/constants'
import { EXTERNAL_OWNER_SYSTEMS } from '@/modules/file/constants'
import lang from '@/lang'
import { debounce, uniqBy } from 'lodash'
import { CssHelper } from '@/modules/base/view/mixin/helpers_mixin'
import { dispatch } from '@/redux/ducks/lists'
import { putDocument, updateDocument, updateDocumentKind } from '@/redux/ducks/document/drawer'
import FilenameInput from '@/modules/base/components/inputs/text-input/filename-input'
import clsx from 'clsx'
import {
  SelectInput,
} from '@/modules/base/components/inputs/index'
import DocumentType from '@/enums/document-type.enum'
import FileService from '@/modules/file/fileService'
import { toast } from '@/modules/base/components/toast/toastConfig'
import FolderSelect from '@/modules/file/components/folder-select'
import DocumentDateInput from '@/modules/document/components/file-info/fields/date-input'
import DocumentTextInput from '@/modules/document/components/file-info/fields/text-input'
import { useAppSelector } from '@/redux/store'
import DocumentAssignmentRouter from '@/modules/document/components/file-info/actions/document-assignment-router'
import CustomerSupplierInfo from '@/modules/document/components/file-info/customer-supplier-info'
import DocumentImageStats from '@/modules/document/components/file-info/document-image-stats'

const useStyles = makeStyles((theme: ThemeInterface) => ({
  content: {
    padding: CssHelper.px(20, 30, 20),
    background: theme.white,
  },
  datevToggle: {
    display: 'flex',
    '& label': {
      marginLeft: 10,
      color: theme.labelLight,
      fontSize: '12px',
      fontWeight: 400,
      marginBottom: '10px',
      letterSpacing: '0.12px',
      height: '14px',
      display: 'block',
    },
    justifyContent: 'space-between',
  },
  toggleRow: {
    display: 'flex',
    margin: CssHelper.px(20, 30),
    paddingTop: 20,
    borderTop: `1px solid ${theme.borderColor}`,
    '& label': {
      marginLeft: 10,
      color: theme.labelLight,
      fontSize: '12px',
      fontWeight: 400,
      marginBottom: '10px',
      letterSpacing: '0.12px',
      height: '14px',
      display: 'block',
    },
  },
  noteInput: {
    padding: CssHelper.px(20, 0),
    '& textarea': {
      height: '100%',
    },
  },
  tagInput: {
    padding: CssHelper.px(20, 0, 0),
    '& > *:last-child': {
      paddingTop: 0,
    },
  },
  controls: {
    display: 'flex',
    flexDirection: 'column',
    paddingBottom: 20,
    '& label': {
      color: theme.labelLight,
      fontSize: '12px',
      fontWeight: 400,
      marginBottom: '10px',
      letterSpacing: '0.12px',
      height: '14px',
      display: 'block',
    },
  },
  row: {
    paddingTop: 20,
    display: 'flex',
    justifyContent: 'space-between',
  },
  noPaddingTop: {
    paddingTop: 0,
  },
  field: {
    '& div': {
      color: theme.black,
      fontSize: theme.fontSizes.default,
      display: 'flex',
      flexDirection: 'column',
    },
  },
  one: {
    flexGrow: 1,
  },
  three: {
    width: 'calc(33% - 12px)',
  },
  disabledDate: {
    pointerEvents: 'none',
    '& input': {
      color: theme.labelLight,
    },
  },
  fill: {
    flexGrow: 1,
    marginLeft: 12,
  },
  disabled: {
    color: theme.labelLight,
  },
  folderSelect: {
    marginTop: 5,
    paddingTop: 20,
  },
  divider: {
    marginTop: 25,
    borderTop: theme.border,
  },
  businessPartnerRow: {
    paddingTop: 20,
  },
  uppercase: {
    '& label': {
      textTransform: 'uppercase',
    },
  },
}))

const DocumentInfoGeneral = () => {
  const classes = useStyles()

  const taxAccountantData = useAppSelector((state) => state.settings.taxAccountant)
  const document = useAppSelector((state) => state.document.drawer.document)
  const contactFormEnabled = useAppSelector((state) => state.document.drawer.contactFormEnabled)
  const contactFormData = useAppSelector((state) => state.document.drawer.contactFormData)
  const folderReadOnly = useAppSelector((state) => state.document.drawer.folderReadOnly)
  const listReadOnly = useAppSelector((state) => state.document.list.isReadOnly)
  const rename = useAppSelector((state) => state.document.drawer.rename)
  const tagOptions = useAppSelector((state) => state.document.list.tagOptions)
  const currentFolder = useAppSelector((state) => state.document.list.currentFolder)
  const [notes, setNotes] = React.useState(document?.notes || '')
  const fromEmail = useAppSelector((state) => state.document?.drawer?.page?.current?.fromEmail)

  const datevTagIds = useMemo(() => tagOptions.filter(({ datevId }) => !!datevId).map(to => to.id), [tagOptions])

  const {
    isAssigned,
    externalOwnerSystem,
    createdBy,
    tags,
    recordDate,
    type,
  } = document

  const isAssignedToDesktop = externalOwnerSystem === EXTERNAL_OWNER_SYSTEMS.desktop
  const kind = document?.tags.find(({ isSystem }) => !!isSystem)

  const showSyncToggle = taxAccountantData?.isAccessGranted && type !== DocumentType.INVOICE
  const showExcludeFromDatevToggle = type !== DocumentType.INVOICE && (document?.isAssigned || document?.tags?.some(tag => datevTagIds.includes(tag.id)))

  const onExcludeFromDatevChange = () => {
    if (folderReadOnly) return
    const value = !document?.excludeFromDatev
    dispatch(updateDocument({ key: 'excludeFromDatev', value }))
    FileService.excludeFromDatev(document.id, value).catch(() => {
      dispatch(updateDocument({ key: 'excludeFromDatev', value: !value }))
      toast.showToast({ type: 'error', message: lang.documentUploadDrawer.excludeFromDatevError })
    })
  }
  const drawerReadOnly = folderReadOnly || listReadOnly
  // orgaMAX Desktop owns this document; the API accepts nothing but notes and tags on it, so the
  // controls that write through the file save are read-only (see modules/file/desktop-owned-file.ts).
  // Two on this tab are deliberately NOT: the „Art", which is stored as a system tag and therefore
  // travels with `tags`, and the DATEV toggle, which has its own route (PATCH
  // /file/:id/excludeFromDatev) and never touches the file save at all.
  const fileReadOnly = drawerReadOnly || isAssignedToDesktop
  const hasBusinessPartner = !!(
    document.customer
    || document.supplier
    || document.customerId
    || document.supplierId
    || document.payee
    || document.metaData?.ocr?.payee
  )
  // FLAG:useDocuAi — the drawer's business-partner linking (CustomerSupplierInfo →
  // SupplierCustomerSelect) is LEGACY functionality: it carries its own "Lieferant
  // anlegen" modal, which creates master data without any of the checks the review
  // enforces (validateReviewPayee's role-specific required fields, the direction hint).
  // Flag-ON tenants decide the business partner in the review, so this stays a
  // legacy-only surface — new baseline features must not leak into the flag-ON path.
  // Flag-OFF is byte-identical: `docuAiEnabled` is false there and the term drops out.
  const docuAiEnabled = !!user?.experimentalFeatures?.includes(experimentalFeatures.useDocuAi)
  const showBusinessPartner = !docuAiEnabled && (contactFormEnabled || hasBusinessPartner)

  const handleNoteChange = debounce((notes) => {
    dispatch(putDocument({ ...document, notes }))
  }, 500)

  const onNotesChange = (nextNotes) => {
    if (drawerReadOnly) return
    setNotes(nextNotes)
    handleNoteChange(nextNotes)
  }

  const onTagChange = (data) => {
    if (drawerReadOnly) return
    dispatch(updateDocument({ key: 'tags', value: uniqBy([...tags.filter(({ isSystem }) => isSystem), ...data], 'label') }))
  }

  const onFolderSelected = (folder) => {
    if (fileReadOnly) return
    const path = folder.path
      ? `${folder.path}/${folder.name}`
      : folder.name

    dispatch(updateDocument({
      value: {
        ...document.metaData,
        path: path ?? null,
      },
      key: 'metaData',
    }))
  }

  const kindOptions = tagOptions.filter(({ isSystem }) => !!isSystem)

  return (
    <PerfectScrollbar className={classes.content}>
      <div>
        <div className={clsx(classes.controls, isAssigned && classes.uppercase)}>
          {type !== DocumentType.INVOICE
            ? <div className={clsx(classes.row, classes.noPaddingTop)}>
              {isAssigned || isAssignedToDesktop
                ? <DocumentTextInput label={lang.documents.drawer.labels.name} name={'name'} value={document.name} />
                : drawerReadOnly
                  ? <DocumentTextInput label={lang.documents.drawer.labels.name} name={'name'} value={document.name} readOnly={true} />
                  : <FilenameInput file={document} isFileRenameOn={rename} onUpdate={(value) => dispatch(updateDocument({ key: 'name', value }))} />}

            </div>
            : null}
          <div className={classes.row}>
            {isAssigned
              ? <div className={classes.field}>
                <label>{lang.tag.kindLabel}</label>
                <div>
                  <span>{kind?.label}</span>
                </div>
              </div>
              : <SelectInput
                useFormik={false}
                name='kindSelect'
                label={`${lang.documentUploadDrawer.kindInputLabel}*`}
                className={classes.one}
                allowCreate={false}
                notAsync={true}
                value={kind}
                options={kindOptions}
                handleChange={(kind) => dispatch(updateDocumentKind(kind))}
                isRequired={true}
                readOnly={drawerReadOnly}
                customSelectOptions={{
                  useDefault: true,
                  isClearable: false,
                  placeholder: lang.documentUploadDrawer.pleaseSelectLabel,
                  valueKey: 'id',
                }}
                showColorBubble={true}
              />}
          </div>
          <div className={classes.row}>
            <DocumentDateInput label={lang.generics.addedAt} value={recordDate} name={'recordDate'} customClass={clsx(classes.three, classes.disabledDate)} />
            <DocumentTextInput label={lang.documents.createdBy} name={'createdBy'} customClass={clsx(classes.fill, classes.disabled)} readOnly={true}
              value={(fromEmail || (createdBy?.firstName ? (createdBy.firstName + (createdBy?.lastName ? ' ' + createdBy.lastName : '')) : createdBy?.email)) ?? ''} />
          </div>
          <DocumentAssignmentRouter readOnly={drawerReadOnly} />
          <div className={classes.divider}></div>
          {showBusinessPartner
            ? (
              <div className={clsx(classes.row, classes.businessPartnerRow)}>
                <CustomerSupplierInfo
                  file={document}
                  customerSupplierFormData={contactFormData}
                  customClass={classes.field}
                  readOnly={fileReadOnly}
                />
              </div>
            )
            : null}
          <div className={classes.folderSelect}>
            <label>{lang.documents.folder.folderSelectLabel}</label>
            <FolderSelect onChange={onFolderSelected} path={document.metaData?.path} currentFolder={currentFolder} readOnly={fileReadOnly} />
          </div>
          <TagSelectInput
            dataQsId='documentInfoGeneral-tags'
            useFormik={false}
            className={classes.tagInput}
            label={lang.tag.label}
            value={document?.tags.filter(({ isSystem }) => !isSystem)}
            category='documents'
            onChange={onTagChange}
            includeSystemTags={false}
            readOnly={drawerReadOnly}
            showColorBubble={true}
          />
          <HtmlInput
            useFormik={false}
            name='notes'
            label={lang.documents.note}
            placeholder={lang.documents.notePlaceholder}
            className={classes.noteInput}
            value={notes}
            onChange={onNotesChange}
            readOnly={drawerReadOnly}
          />
          {/* FLAG:useDocuAi — embedded-image OCR stats (self-gates, renders null on baseline) */}
          <DocumentImageStats document={document} />
        </div>
      </div>
      {showExcludeFromDatevToggle && (
        <div className={clsx(classes.datevToggle)}>
          <label>{lang.documentUploadDrawer.excludeFromDatevLabel}</label>
          <ToggleSlider
            useFormik={false}
            checked={document?.excludeFromDatev}
            onChange={onExcludeFromDatevChange}
            readOnly={folderReadOnly}
          />
        </div>
      )}
      {showSyncToggle && (
        <div className={clsx(classes.toggleRow, isAssigned && classes.uppercase)}>
          <ToggleSlider
            useFormik={false}
            checked={document?.syncToMbk}
            onChange={() => !fileReadOnly && dispatch(updateDocument({ key: 'syncToMbk', value: !document?.syncToMbk }))}
            readOnly={fileReadOnly}
          />
          <label>{lang.documentUploadDrawer.toggleLabel}</label>
        </div>
      )}
    </PerfectScrollbar>
  )
}

export default DocumentInfoGeneral
